import React from 'react';
import {UserTalkingPoint, VoteType} from '../declarations/backend/backend.did';
import {BackendActor} from '../lib/agent';
import {VoteEnum} from '../types';
import {useAuth} from './auth';

type VotingContextType =
  | {
      getTalkingPoints: () => Promise<UserTalkingPoint[] | undefined>;
      vote: (talkingPointId: string, vote: VoteEnum) => Promise<any>;
      currentTalkingPointId: string;
      changeCurrentTalkingPointId: (id: string) => void;
      resetVotes: () => Promise<any>;
    }
  | undefined;

const VotingContext = React.createContext<VotingContextType>(undefined);
VotingContext.displayName = 'VotingContext';

function VotingProvider(props: {children: React.ReactNode}) {
  const {identity} = useAuth();
  const [currentTalkingPointId, setCurrentTalkingPointId] = React.useState<string>('1');

  const changeCurrentTalkingPointId = React.useCallback(
    (id: string) => {
      setCurrentTalkingPointId(id);
    },
    [setCurrentTalkingPointId]
  );

  const getTalkingPoints = React.useCallback(async () => {
    try {
      if (!identity) {
        throw new Error('identity is not defined');
      }
      const backend = await BackendActor.getBackendActor(identity);
      const talkingPoints = await backend.getTalkingPoints();
      return talkingPoints;
    } catch (error) {
      console.error(error);
    }
  }, [identity]);

  const resetVotes = React.useCallback(async () => {
    if (!identity) {
      throw new Error('identity is not defined');
    }
    const backend = await BackendActor.getBackendActor(identity);
    await backend.resetMyVotes();
  }, [identity]);

  const vote = React.useCallback(
    async (talkingPointId: string, vote: VoteEnum) => {
      try {
        if (!identity) {
          throw new Error('identity is not defined');
        }
        const backend = await BackendActor.getBackendActor(identity);
        let voteObj: VoteType = {
          Yes: null,
        };
        if (vote === 'no') {
          voteObj = {
            No: null,
          };
        }
        if (vote === 'abstain') {
          voteObj = {
            Abstain: null,
          };
        }
        await backend.vote(talkingPointId, voteObj);
      } catch (error) {
        console.error(error);
      }
    },
    [identity]
  );

  const value = React.useMemo(() => {
    return {getTalkingPoints, vote, resetVotes, currentTalkingPointId, changeCurrentTalkingPointId};
  }, [changeCurrentTalkingPointId, currentTalkingPointId, getTalkingPoints, resetVotes, vote]);

  return <VotingContext.Provider {...props} value={value} />;
}

function useVoting() {
  const context = React.useContext(VotingContext);
  if (context === undefined) {
    throw new Error(`useVoting must be used within a VotingProvider`);
  }
  return context;
}

export {VotingProvider, useVoting};
